import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {catchError, map, retry} from 'rxjs/operators';
import {BehaviorSubject, Observable, of, throwError} from 'rxjs';

import {environment} from 'src/environments/environment.generated';
import {NoticeInfo, QueryParams} from 'src/app/core/interfaces/common';
import {CommonEnvironment} from "../../../environments/environment.common";
import {getAppThemeName} from "../../core/functions/common.function";

@Injectable({
  providedIn: 'root'
})
export class CommonService {

  private _langList = ['de', 'en'];
  public _isUserLoggedin = new BehaviorSubject(false);
  public _isIdpTokenSet = new BehaviorSubject(false);
  public _isInternalAuthSet = new BehaviorSubject(false);
  public _isFknKeySet = new BehaviorSubject(false);
  public _langSelectionEnabled = new BehaviorSubject(true);
  public _languageData = new BehaviorSubject({
    langList: getAppThemeName() === 'db' ? this.getOrderedLangList() : ['de'],
    activeLang: sessionStorage.getItem('lang') || 'de'
  });
  public _consentObject = new BehaviorSubject<any>({});
  public _permissionDetails = new BehaviorSubject<any>({});
  public _noticeAcceptance = new BehaviorSubject<NoticeInfo>({cookieAccepted: false});
  public _showHeaderAndFooter = new BehaviorSubject<boolean>(false);

  constructor(
    private httpClient: HttpClient
  ) {
  }

  private getOrderedLangList() {
    const elementIndex = this._langList.indexOf(sessionStorage.getItem('lang') || 'de');
    this._langList[elementIndex] = this._langList[0];
    this._langList[0] = sessionStorage.getItem('lang') || 'de';
    return this._langList;
  }

  private createAPIHeader() {
    let headerObject: any = {
      'Content-Type': 'application/json',
      'apiKey': environment.apiKey,
      'X-Request-ID': sessionStorage.getItem("x_request_id")
    };

    return new HttpHeaders(headerObject);
  }

  changeUserLoginStatus(isEnable: boolean) {
    sessionStorage.setItem('isUserLoggedin', `${isEnable}`);
    this._isUserLoggedin.next(isEnable);
  }

  changeShowHeaderAndFooter(isShown: boolean) {
    this._showHeaderAndFooter.next(isShown);
  }

  getShowHeaderAndFooter() {
    return this._showHeaderAndFooter.value;
  }

  changeNoticeAcceptanceStatus(noticeInfo: NoticeInfo) {
    this._noticeAcceptance.next(noticeInfo);
  }

  changeIDPUserAuth(isEnable: boolean) {
    this._isIdpTokenSet.next(isEnable);
  }

  changeInternalUserAuth(isEnable: boolean) {
    this._isInternalAuthSet.next(isEnable);
  }

  changeFknStatus(isEnable: boolean) {
    this._isFknKeySet.next(isEnable);
  }

  setConsentObject(object: any) {
    this._consentObject.next(object);
  }

  setPermissionObject(object: any) {
    this._permissionDetails.next(object);
  }

  setActiveLang(lngList: Array<string>, activeLang: string) {
    this._languageData.next({langList: lngList, activeLang: activeLang});
  }

  getRequestExternal(endpoint: string | undefined, urlExtParams: QueryParams[] = [], queryParams: QueryParams[]): Observable<any> {
    if (urlExtParams.length) {
      urlExtParams.forEach((obj: QueryParams) => {
        endpoint += `/${obj.paramValue}`;
      });
    }

    let params = '';
    if (queryParams.length) {
      params = '?';
      queryParams.forEach((obj: QueryParams) => {
        params += `${obj.paramName}=${obj.paramValue}&`;
      });
      params = params.slice(0, -1);
    }

    const url = environment.apiBaseUrl + endpoint + params;
    const requestHeader = {
      headers: this.createAPIHeader(),
      withCredentials: true
    };

    return this.httpClient.get(url, requestHeader).pipe(
      map(resp => resp),
      retry(2),
      catchError((err) => {
        sessionStorage.removeItem('selectedFknKey');
        this.handleError<any>([]);
        return throwError(err);
      })
    );
  }

  postRequestExternal(endpoint: string | undefined, payload = {}, urlExtParams: QueryParams[] = [], apiCallingForInternalAuth = false, token: string | null): Observable<any> {
    let headerObject: HttpHeaders = this.createAPIHeader();
    if (endpoint === CommonEnvironment.endPoints.postForResource && token) {
      headerObject = headerObject.append('Consent-Request-Jwt', `${token}`);
    }
    if (urlExtParams.length) {
      urlExtParams.forEach((obj: QueryParams) => {
        endpoint += `/${obj.paramValue}`;
      });
    }
    const apiBaseUrl = apiCallingForInternalAuth ? environment.apiBaseUrl.replace('cmp/', '') : environment.apiBaseUrl;

    const requestUrl = apiBaseUrl + endpoint;

    const requestHeader = {
      headers: headerObject,
      withCredentials: true
    };
    return this.httpClient.post(requestUrl, payload, requestHeader).pipe(
      map(res => res),
      catchError((err: any) => {
        sessionStorage.removeItem('selectedFknKey');
        this.handleError<any>(err);
        return throwError(err);
      })
    );
  }

  postToCIAM(uri: string, token: any) {
    const hiddenForm = document.createElement('form');
    hiddenForm.method = 'POST';
    hiddenForm.action = uri;
    hiddenForm.style.display = 'none';
    hiddenForm.append('Content-Type', 'application/x-www-form-urlencoded');

    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = 'consent_response';
    hiddenInput.value = token;
    hiddenForm.appendChild(hiddenInput);

    document.body.appendChild(hiddenForm);
    hiddenForm.submit();
  }

  patchRequestExternal(endpoint: string | undefined, payload = {}, urlExtParams: QueryParams[] = []): Observable<any> {
    if (urlExtParams.length) {
      urlExtParams.forEach((obj: QueryParams) => {
        endpoint += `/${obj.paramValue}`;
      });
    }
    const url = environment.apiBaseUrl + endpoint;

    const requestHeader = {
      headers: this.createAPIHeader(),
      withCredentials: true
    };
    return this.httpClient.patch(url, payload, requestHeader).pipe(
      map(res => res),
      catchError(this.handleError<any>([]))
    );
  }

  deleteRequestExternal(endpoint: string | undefined, urlExtParams: QueryParams[] = [], queryParams: QueryParams[]): Observable<any> {
    if (urlExtParams.length) {
      urlExtParams.forEach((obj: QueryParams) => {
        endpoint += `/${obj.paramValue}`;
      });
    }

    let params = '';
    if (queryParams.length) {
      params = '?';
      queryParams.forEach((obj: QueryParams) => {
        params += `${obj.paramName}=${obj.paramValue}&`;
      });
      params = params.slice(0, -1);
    }

    const url = environment.apiBaseUrl + endpoint + params;
    const requestHeader = {
      headers: this.createAPIHeader(),
      withCredentials: true
    };

    return this.httpClient.delete(url, requestHeader).pipe(
      map(res => res),
      catchError((err: any) => {
        sessionStorage.removeItem('selectedFknKey');
        this.handleError<any>(err);
        return throwError(err);
      })
    );
  }

  private handleError<T>(result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      console.log(`Failed: ${error.message}`);
      return of(result as T);
    }
  }
}
